import { useState, useMemo, useDeferredValue, ChangeEvent, useCallback } from "react";
import { Table, TableBody, TableHead, TableHeader, TableRow, Checkbox } from "@libs/zapi-ui/shadcn";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { AppButton, SearchInput, LoadingSpinner, FigmaTypography } from "@libs/zapi-ui/custom";
import { InviteUserModal } from "./InviteUserModal";
import { ManageAccessDrawer } from "./ManageAccessDrawer";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getOrganizationData } from "../services/getOrganizationData";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TGetUsersResponse, TUser } from "../services/schemas";
import { TeamTableRow } from "./TeamTableRow";

export function TeamTable() {
  const { t } = useTranslation("settings");
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAccessDrawerOpen, setIsAccessDrawerOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<TUser | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organizationId: defaultOrganizationId, id: currentUserId, role } = useMeStore();
  const { currentOrganizationId } = useOrganizationStore();

  const organizationId =
    isSystemAdmin(role) && currentOrganizationId > 0 ? currentOrganizationId : defaultOrganizationId;

  const { data: organizationData, isLoading: isLoadingOrganizationData } = useQuery<TGetUsersResponse, Error>({
    queryKey: ["organizationData", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      try {
        const response = await getOrganizationData(organizationId);
        return response.map((user) => ({ ...user, createdAt: new Date(user.createdAt) }));
      } catch (error) {
        console.error(error);
        toast.error(t("teamTable.fetchFailed"));
        navigate(getRouteProjects());
        throw error;
      }
    },
    enabled: !!organizationId
  });

  const filteredTeamMembers = useMemo(() => {
    if (!organizationData) return [];
    if (!deferredSearchQuery) return organizationData;
    return organizationData.filter(
      (member) =>
        member.firstName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.email.toLowerCase().startsWith(deferredSearchQuery.toLowerCase())
    );
  }, [deferredSearchQuery, organizationData]);

  const displayedMemberIds = useMemo(() => filteredTeamMembers.map((member) => member.id), [filteredTeamMembers]);

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) setSelectedRows(displayedMemberIds);
    else setSelectedRows([]);
  };

  const handleSelectRow = useCallback((id: number) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  }, []);

  const handleRemoveSelectedUsers = () => {
    toast.success(`${selectedRows.length} user(s) submitted for removal.`);
    setSelectedRows([]);
  };

  const handleOpenManageAccess = useCallback((user: TUser) => {
    setSelectedUserForAccess(user);
    setIsAccessDrawerOpen(true);
  }, []);

  const handleDrawerClose = (openState: boolean) => {
    setIsAccessDrawerOpen(openState);
    if (!openState) {
      setSelectedUserForAccess(null);
    }
  };

  const isAllSelected = displayedMemberIds.length > 0 && selectedRows.length === displayedMemberIds.length;

  const currentPage = 1;
  const totalPages = 1;

  if (isLoadingOrganizationData || !organizationId) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4 w-full">
      <div>
        <FigmaTypography component="h1" variant="heading-2">{t("teamTable.heading")}</FigmaTypography>
      </div>
      <div className="flex items-center justify-between">
        <SearchInput
          placeholder={t("teamTable.searchPlaceholder")}
          className="w-[320px]"
          value={searchQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        />
        <div className="flex items-center space-x-3">
          {selectedRows.length > 0 ? (
            <>
              <span className="text-[14px] text-zinc-900 font-medium">
                {t("teamTable.usersSelected", { count: selectedRows.length })}
              </span>
              <AppButton color="zinc150" onClick={handleRemoveSelectedUsers}>
                <Trash2 className="h-4 w-4" />
                {t("teamTable.remove")}
              </AppButton>
            </>
          ) : (
            <AppButton onClick={() => setIsInviteModalOpen(true)}>
              <Plus className="h-4 w-4" />
              {t("teamTable.user")}
            </AppButton>
          )}
        </div>
      </div>
      <div className="rounded-md border w-full h-[calc(100vh-300px)] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label={t("teamTable.selectAll")}
                  disabled
                />
              </TableHead>
              <TableHead className="w-[250px] normal-case">{t("teamTable.user")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.role")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.projects")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.accountStatus")}</TableHead>
              {/* <TableHead className="normal-case">Last Login</TableHead> */}
              <TableHead className="normal-case">{t("teamTable.dateAdded")}</TableHead>
              <TableHead className="w-[150px] normal-case">{t("teamTable.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTeamMembers.map((member) => {
              return (
                <TeamTableRow
                  key={member.id}
                  member={member}
                  organizationId={organizationId}
                  currentUserId={currentUserId}
                  selectedRows={selectedRows}
                  handleSelectRow={handleSelectRow}
                  handleOpenManageAccess={handleOpenManageAccess}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center space-x-2">
          <AppButton appearance="text" size="sm" disabled={currentPage === 1}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t("teamTable.previous")}
          </AppButton>
          {[1].map((page) => (
            <AppButton
              key={page}
              appearance={currentPage === page ? "contained" : "text"}
              size="sm"
            >
              {page}
            </AppButton>
          ))}
          {totalPages > 1 && <span className="text-[14px]">...</span>}
          {totalPages > 1 && (
            <AppButton
              appearance={currentPage === totalPages ? "contained" : "text"}
              size="sm"
            >
              {totalPages}
            </AppButton>
          )}
          <AppButton appearance="text" size="sm" disabled={currentPage === totalPages}>
            {t("teamTable.next")}
            <ChevronRight className="ml-1 h-4 w-4" />
          </AppButton>
        </div>
        <span className="text-[14px] text-muted-foreground">
          {t("teamTable.showingResults", { shown: filteredTeamMembers.length, total: organizationData?.length || 0 })}
        </span>
      </div>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
        organizationId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
      <ManageAccessDrawer
        open={isAccessDrawerOpen}
        onOpenChange={handleDrawerClose}
        user={selectedUserForAccess as TUser}
        availableProjects={[]}
        orgId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
    </div>
  );
}
